import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AGENT_SETUP_BASE } from '../../constants/setupRoadmapSteps';
import WizardModeSelector from './wizard/WizardModeSelector';
import WizardStep from './wizard/WizardStep';
import AgentForm from './AgentForm';
import CollectionSelector from './CollectionSelector';
import ToolSelector from './ToolSelector';
import { getAllTemplates } from './wizard/PromptTemplates';
import api from '../../API.js';
import styles from '../../styles/agents.module.css';

function AgentCreationWizard({ onComplete, onCancel }) {
    const navigate = useNavigate();
    const [mode, setMode] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        systemPrompt: '',
        rag_collections: [],
        tools: [],
        defaultModel: '',
        defaultTopK: 5,
        defaultMinSimilarity: 0.7,
        isActive: true
    });
    const [availableModels, setAvailableModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Check for remembered preference
        const remembered = localStorage.getItem('agentWizardMode');
        if (remembered && (remembered === 'testing' || remembered === 'production')) {
            setMode(remembered);
        }
        fetchModels();
    }, []);

    const fetchModels = async () => {
        try {
            const response = await fetch('/llmapi/api/tags');
            const data = await response.json();
            if (data.models && data.models.length > 0) {
                const modelNames = data.models.map(m => m.name);
                setAvailableModels(modelNames);
                // Set default model
                const llmModels = modelNames.filter(m => 
                    !m.includes('embed') && 
                    !m.includes('embedding') &&
                    !m.startsWith('nomic-embed')
                );
                if (llmModels.length > 0) {
                    setFormData(prev => ({ ...prev, defaultModel: llmModels[0] }));
                } else if (modelNames.length > 0) {
                    setFormData(prev => ({ ...prev, defaultModel: modelNames[0] }));
                }
            }
        } catch (err) {
            console.error('Error fetching models:', err);
        }
    };

    const getSteps = () => {
        if (mode === 'testing') {
            return [
                { id: 'basic', title: 'Basic Information', optional: false },
                { id: 'prompt', title: 'System Prompt', optional: false },
                { id: 'collections', title: 'Knowledge Base', optional: true, encourage: true },
                { id: 'tools', title: 'Tools', optional: true, encourage: true },
                { id: 'model', title: 'Model Configuration', optional: false },
                { id: 'review', title: 'Review & Create', optional: false }
            ];
        } else {
            return [
                { id: 'basic', title: 'Basic Information', optional: false },
                { id: 'prompt', title: 'System Prompt', optional: false },
                { id: 'collections', title: 'Knowledge Base', optional: true },
                { id: 'tools', title: 'Tools', optional: true },
                { id: 'model', title: 'Model Configuration', optional: false },
                { id: 'review', title: 'Review & Create', optional: false }
            ];
        }
    };

    const steps = getSteps();
    const totalSteps = steps.length;

    const handleNext = () => {
        if (currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleCreate();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSkip = () => {
        handleNext();
    };

    const canProceed = () => {
        const step = steps[currentStep];
        if (step.id === 'basic') {
            return formData.name.trim().length > 0;
        }
        if (step.id === 'prompt') {
            return formData.systemPrompt.trim().length > 0;
        }
        if (step.id === 'model') {
            return formData.defaultModel.trim().length > 0;
        }
        return true;
    };

    const handleCreate = async () => {
        setLoading(true);
        setError('');

        try {
            // Convert rag_collections to RAG tool if tools array is empty but collections exist
            let tools = formData.tools || [];
            if (tools.length === 0 && formData.rag_collections && formData.rag_collections.length > 0) {
                tools = [{
                    type: 'rag',
                    config: { collections: formData.rag_collections }
                }];
            }

            const submitData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                systemPrompt: formData.systemPrompt.trim(),
                tools: tools,
                rag_collections: formData.rag_collections,
                defaultModel: formData.defaultModel || 'llama3.2',
                defaultTopK: formData.defaultTopK,
                defaultMinSimilarity: formData.defaultMinSimilarity,
                isActive: formData.isActive
            };

            const response = await api.post('agent', { body: submitData });

            if (response.status !== 200) {
                let errorMessage = `HTTP error! status: ${response.status}`;
                if (response.data) {
                    if (typeof response.data === 'string') {
                        errorMessage = response.data;
                    } else if (response.data.message) {
                        errorMessage = response.data.message;
                    } else if (response.data.error) {
                        errorMessage = response.data.error;
                    }
                }
                throw new Error(errorMessage);
            }

            if (onComplete) {
                onComplete(response.data);
            } else {
                navigate(`${AGENT_SETUP_BASE}/manage`);
            }
        } catch (err) {
            setError(err.message || 'Failed to create agent');
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        const step = steps[currentStep];

        switch (step.id) {
            case 'basic':
                return (
                    <div>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                                Agent Name
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                className={styles.formInput}
                                placeholder="e.g., Research Assistant"
                                autoFocus
                            />
                            {mode === 'testing' && (
                                <div className={styles.formHelp}>
                                    💡 Tip: Choose a descriptive name that reflects the agent's purpose
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Description
                            </label>
                            <input
                                type="text"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                className={styles.formInput}
                                placeholder="Brief description of what this agent does"
                            />
                            {mode === 'testing' && (
                                <div className={styles.formHelp}>
                                    💡 Tip: A good description helps others understand the agent's role
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'prompt':
                const templates = getAllTemplates();
                return (
                    <div>
                        {mode === 'testing' && (
                            <div className={styles.info} style={{ marginBottom: '1.5em' }}>
                                <strong>💡 Try different templates:</strong> Select a template below or write your own custom prompt.
                            </div>
                        )}

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Prompt Template
                            </label>
                            <select
                                value={formData.selectedTemplate || 'custom'}
                                onChange={(e) => {
                                    const template = templates.find(t => t.id === e.target.value);
                                    if (template && template.prompt) {
                                        setFormData(prev => ({ 
                                            ...prev, 
                                            systemPrompt: template.prompt,
                                            selectedTemplate: template.id
                                        }));
                                    }
                                }}
                                className={styles.formInput}
                                style={{ marginBottom: '1em' }}
                            >
                                {templates.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.name} - {t.description}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                                System Prompt
                            </label>
                            <textarea
                                value={formData.systemPrompt}
                                onChange={(e) => setFormData(prev => ({ ...prev, systemPrompt: e.target.value }))}
                                className={`${styles.formInput} ${styles.formTextarea}`}
                                rows={10}
                                placeholder="Enter the system prompt that defines how the agent behaves..."
                            />
                            <div className={styles.formHelp}>
                                {formData.systemPrompt.length} characters
                            </div>
                        </div>
                    </div>
                );

            case 'collections':
                return (
                    <div>
                        {mode === 'testing' && (
                            <div className={styles.info} style={{ marginBottom: '1.5em' }}>
                                <strong>🧪 Try it:</strong> Add collections to enable the Knowledge Base (RAG) tool. This allows the agent to search through your documents.
                            </div>
                        )}
                        <CollectionSelector
                            selectedCollections={formData.rag_collections}
                            onChange={(collections) => setFormData(prev => ({ ...prev, rag_collections: collections }))}
                        />
                    </div>
                );

            case 'tools':
                return (
                    <div>
                        {mode === 'testing' && (
                            <div className={styles.info} style={{ marginBottom: '1.5em' }}>
                                <strong>🧪 Try it:</strong> Add tools like PubMed Search to give your agent additional capabilities. Tools extend what the agent can do beyond just answering questions.
                            </div>
                        )}
                        <ToolSelector
                            selectedTools={formData.tools}
                            onChange={(tools) => setFormData(prev => ({ ...prev, tools }))}
                        />
                    </div>
                );

            case 'model':
                return (
                    <div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Default Model
                            </label>
                            {availableModels.length > 0 ? (
                                <select
                                    value={formData.defaultModel}
                                    onChange={(e) => setFormData(prev => ({ ...prev, defaultModel: e.target.value }))}
                                    className={styles.formInput}
                                >
                                    <option value="">Select a model...</option>
                                    {availableModels
                                        .filter(m => !m.includes('embed') && !m.includes('embedding') && !m.startsWith('nomic-embed'))
                                        .map(model => (
                                            <option key={model} value={model}>{model}</option>
                                        ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={formData.defaultModel}
                                    onChange={(e) => setFormData(prev => ({ ...prev, defaultModel: e.target.value }))}
                                    className={styles.formInput}
                                    placeholder="e.g., llama3.2"
                                />
                            )}
                            <div className={styles.formHelp}>
                                Default LLM model to use for this agent
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Top K
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={formData.defaultTopK}
                                    onChange={(e) => setFormData(prev => ({ ...prev, defaultTopK: parseInt(e.target.value) || 5 }))}
                                    className={styles.formInput}
                                />
                                <div className={styles.formHelp}>
                                    Number of documents to retrieve
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Min Similarity
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={formData.defaultMinSimilarity}
                                    onChange={(e) => setFormData(prev => ({ ...prev, defaultMinSimilarity: parseFloat(e.target.value) || 0.7 }))}
                                    className={styles.formInput}
                                />
                                <div className={styles.formHelp}>
                                    Minimum similarity threshold (0-1)
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'review':
                const toolCount = formData.tools?.length || (formData.rag_collections?.length > 0 ? 1 : 0);
                return (
                    <div>
                        <div className={styles.info} style={{ marginBottom: '1.5em' }}>
                            <strong>Review your agent configuration:</strong> Make sure everything looks correct before creating.
                        </div>

                        <div className={styles.formSection}>
                            <h3 className={styles.formSectionTitle}>Agent Summary</h3>
                            <div style={{ display: 'grid', gap: '1em' }}>
                                <div>
                                    <strong>Name:</strong> {formData.name || '(not set)'}
                                </div>
                                {formData.description && (
                                    <div>
                                        <strong>Description:</strong> {formData.description}
                                    </div>
                                )}
                                <div>
                                    <strong>Model:</strong> {formData.defaultModel || 'llama3.2'}
                                </div>
                                <div>
                                    <strong>Tools:</strong> {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
                                </div>
                                {formData.rag_collections.length > 0 && (
                                    <div>
                                        <strong>Collections:</strong> {formData.rag_collections.join(', ')}
                                    </div>
                                )}
                                <div>
                                    <strong>Status:</strong> {formData.isActive ? 'Active' : 'Inactive'}
                                </div>
                            </div>
                        </div>

                        {mode === 'testing' && (
                            <div className={styles.info} style={{ marginTop: '1.5em' }}>
                                <strong>🧪 Testing Tip:</strong> After creating, you can test the agent immediately from the agent management page.
                            </div>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    if (!mode) {
        return (
            <WizardModeSelector
                onSelect={setMode}
                rememberPreference={true}
            />
        );
    }

    const step = steps[currentStep];
    const stepDescription = mode === 'testing' && step.encourage
        ? `This step is optional but recommended. ${step.id === 'collections' ? 'Try adding a collection to enable the Knowledge Base tool.' : 'Try adding a tool to extend the agent\'s capabilities.'}`
        : step.optional
        ? 'This step is optional. You can skip it if you don\'t need this feature.'
        : null;

    return (
        <div className={styles.container}>
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            <WizardStep
                stepNumber={currentStep + 1}
                totalSteps={totalSteps}
                title={step.title}
                description={stepDescription}
                isOptional={step.optional}
                canProceed={canProceed()}
                onNext={handleNext}
                onBack={handleBack}
                onSkip={step.optional ? handleSkip : null}
            >
                {renderStepContent()}
            </WizardStep>
        </div>
    );
}

export default AgentCreationWizard;
